export interface BundlePlan {
  id: string
  ussd_plan_code: number
  bundle_id: string
  plan_types: string[]
  plan_name: string
  amount_incl_tax: number
  amount_excl_tax: number
}

interface BundlePlanCardProps {
  plan: BundlePlan
  selected: boolean
  onSelect: (id: string) => void
}

const formatAmount = (amount: number) => Number(amount || 0).toFixed(2)

const PLAN_TYPE_ICONS: Record<string, string> = {
  voice: 'voice',
  data: 'data',
  sms: 'sms',
}

export default function BundlePlanCard({ plan, selected, onSelect }: BundlePlanCardProps) {
  return (
    <div
      className={`border rounded-2xl p-4 flex flex-col gap-4 cursor-pointer transition-all duration-200 ${
        selected
          ? 'border-p_primary shadow-md'
          : 'border-p_light_hover hover:border-p_primary hover:shadow-md'
      }`}
      onClick={() => onSelect(plan.id)}
      data-testid={`bundle-plan-${plan.id}`}
    >
      <div className="flex items-start gap-4">
        <div className="w-[120px]" data-testid={`plan-ussd-code-${plan.id}`}>
          <p className="text-medium text-[#777777]">USSD Plan Code</p>
          <p className="text-body1 text-primary_text font-semibold">{plan.ussd_plan_code}</p>
        </div>

        <div className="flex-1 flex justify-end gap-1.5 flex-wrap">
          {plan.plan_types?.map((type) => (
            <span
              key={type}
              className="flex items-center gap-1 bg-information_light rounded-[10px] px-2 py-1 text-medium font-semibold text-p_primary"
              data-testid={`plan-type-${plan.id}-${type.toLowerCase()}`}
            >
              {PLAN_TYPE_ICONS[type.toLowerCase()] && (
                <img
                  src={`/images/${PLAN_TYPE_ICONS[type.toLowerCase()]}.png`}
                  alt={type}
                  className="w-6 h-6"
                />
              )}
              {type}
            </span>
          ))}
        </div>

        <div className="w-px self-stretch bg-p_light_hover" />

        <div className="w-[135px] text-right" data-testid={`plan-amount-${plan.id}`}>
          <p className="text-medium text-[#777777]">Amount</p>
          <p className="text-heading3 text-primary_text">{formatAmount(plan.amount_incl_tax)} AFA</p>
          <p className="text-medium text-[#777777]">Incl. tax</p>
        </div>

        <div className="pt-1">
          <div
            className={`relative w-5 h-5 rounded-full shrink-0 border-2 ${
              selected ? 'border-p_primary' : 'border-p_light_hover'
            }`}
            data-testid={`plan-radio-${plan.id}`}
          >
            {selected && (
              <div className="absolute top-1/2 left-1/2 w-3 h-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-p_primary" />
            )}
          </div>
        </div>
      </div>

      <div className="bg-information_light rounded-lg p-2 min-w-0" data-testid={`plan-name-${plan.id}`}>
        <p className="text-body1 text-primary_text break-words">
          <span className="text-medium text-[#777777] block">Plan Name</span>
          {plan.plan_name}
        </p>
      </div>
    </div>
  )
}
